import { useActionLogSender } from '@framework/action-log';
import { Button } from '@framework/ui/atoms/Button';
import { GeneralModal } from '@framework/ui/elements/GeneralModal';
import type { ViewModelEntity } from '@view-model/domain/view-model';
import { ViewModelOperation } from '@view-model/domain/view-model';
import type { Folder } from '@workspace/view-model-folder/domain/Folder';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

type Props = {
  isOpen: boolean;
  onClose(): void;
  viewModel: ViewModelEntity;
  destinationFolder: Folder;
};

export const MoveViewModelConfirmModal: React.FC<Props> = ({
  isOpen,
  onClose,
  viewModel,
  destinationFolder,
}: Props) => {
  const { t } = useTranslation();
  const [confirmed, setConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const actionLogSender = useActionLogSender();

  useEffect(() => {
    setConfirmed(false);
    setIsSubmitting(false);
  }, [isOpen]);

  const handleSubmit = useCallback(async () => {
    if (!confirmed) return;

    setIsSubmitting(true);
    try {
      await ViewModelOperation.move(viewModel.workspaceId, viewModel.id, destinationFolder.id, actionLogSender);
      toast.success(t('workspace.moveViewModel.success'));
      onClose();
    } catch (e) {
      toast.error(t('workspace.moveViewModel.error'));
      throw e;
    } finally {
      setIsSubmitting(false);
    }
  }, [actionLogSender, confirmed, destinationFolder.id, onClose, viewModel.id, viewModel.workspaceId, t]);

  return (
    <GeneralModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('workspace.modal.moveConfirmTitle')}
      submitButton={
        <Button color="primary" onClick={handleSubmit} disabled={!confirmed} loading={isSubmitting}>
          {t('workspace.modal.move')}
        </Button>
      }
      cancelButton={
        <Button color="secondary" onClick={onClose} disabled={isSubmitting}>
          {t('common.action.cancel')}
        </Button>
      }
    >
      <div>
        <p>
          {t('workspace.moveViewModel.confirmMessage', {
            name: `${viewModel.name}`,
            destination: destinationFolder.isRoot()
              ? t('workspace.modal.workspaceRoot')
              : t('workspace.modal.underFolder', { folderName: destinationFolder.name }),
          })}
        </p>
        <label className="block">
          <input
            type="checkbox"
            className="form-checkbox mr-1 size-6 border-brand"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          />
          {t('workspace.modal.moveConfirmCheck')}
        </label>
      </div>
    </GeneralModal>
  );
};
